import { cn, DateTile, OrganizerMark, formatEventWeekdayTime, readerTimezone } from '@h2hspark/ui';
import { ArrowRight, MapPin, Users } from 'lucide-react';
import * as React from 'react';
import { Link } from 'react-router-dom';

import { cardAccentFor, cityLine, eventPagePath, goingCountLabel } from './constants';
import type { DiscoverEventCard } from './types';

/**
 * One event in the Discover grid (`MEM-014` AC-2).
 *
 * ## Transcribed from `Web App.dc.html` (10 September), the event card (lines 243–255)
 *
 * The prototype uses no class names — every element is inline `style` over CSS custom properties —
 * so this is a transcription of those values onto the tokens `packages/ui/tailwind-preset.ts`
 * registers for them. Card: `--surface-card`, `1px solid var(--border-subtle)`, `--radius-xl`,
 * `--shadow-sm`, `overflow:hidden`, a column. A **5px accent bar** across the top in one of the
 * design's four gradients (`cardAccentFor`). Body `padding:20px`. Row one: the soft **date tile**
 * (`DateTile variant="soft"`), the name as an `<h3>` at `var(--text-lg)` over the weekday and time
 * at `var(--text-xs)` in `--text-muted`, and the atmosphere pill (`--surface-sunken`,
 * `--text-body`, 700 at `var(--text-2xs)`, `5px 11px`). Row two: `map-pin` and the city, `users`
 * and *"N going"*, at `var(--text-sm)` in `--text-muted`. Row three: the interest tags (600 at
 * `var(--text-xs)`, `--surface-sunken`, `5px 10px`). A footer on a `--divider` rule with *View →*
 * at the right in `--brand-primary`.
 *
 * ## What the prototype's card has that this one does not, and why
 *
 * **The attendee faces.** The prototype's footer draws three attendees' avatars — the same three
 * initials on every card. Sumit ruled them off the card on 2026-09-11 (*"ok"*), in line with the
 * same day's ruling that Who's coming is for registrants; `handoff-corrections.md` #122. Their
 * slot carries the **organizer** instead — their gradient initials and name, the prototype's own
 * organizer treatment — because the host is one of the card's settled fields and the design's
 * card has nowhere else for it.
 *
 * **The favourite heart**, which the phone design draws. `MEM-014` AC-6: saving or favouriting an
 * event is not built. See `SAVING_AN_EVENT_IS_NOT_BUILT` in `constants.ts`.
 *
 * ## What changed from the card before it
 *
 * **The going count is drawn**, as the prototype draws it — reversing a decision this file used to
 * record (*"a count on the card would make attendance a browsing signal"*), on Sumit's ruling of
 * 2026-09-11: the count stays *"under the existing rules for when a going count may show"*. So it
 * arrives only for a signed-in member, from the roster module's own figure (`MEM-011` AC-3,
 * `useGoingCounts`), and never as a zero (`goingCountLabel`). A signed-out visitor sees no count
 * and no zero (`MEM-019` AC-2).
 *
 * **The cover image is no longer on the card.** The 10 September card has none — the accent bar
 * is its only colour — and the image stays where the prototype puts it, on the event page
 * (`EVT-008`). The pull request lists it for Sumit, because the organizer's Media step promises
 * the photo does its work *"on a crowded listing page"*.
 *
 * **The street address is no longer on the card**: the prototype writes the city, and the address
 * is the event page's (`MEM-016`).
 */
export function EventCard({
  card,
  goingCount,
}: {
  readonly card: DiscoverEventCard;
  /** The roster's count, for a signed-in member; `undefined` for a visitor or while it loads. */
  readonly goingCount?: number | undefined;
}): React.ReactElement {
  const reader = React.useMemo(() => readerTimezone(), []);
  const when = formatEventWeekdayTime({
    startsAt: card.startsAt,
    timezone: card.timezone,
    reader,
  });
  const city = cityLine(card);
  const going = goingCountLabel(goingCount);

  return (
    <Link
      to={eventPagePath(card)}
      className={cn(
        // **No `h-full`, and the grid does not stretch** (Sumit, 2026-09-12: *"lets"*, to the
        // offered fix). Equal-height cards put a card with no city and no tags in a box sized by
        // its tallest neighbour, so its organizer-and-View footer floated at the bottom under a
        // band of nothing. A card is now as tall as what is in it, and the row aligns to its top.
        'flex flex-col overflow-hidden rounded-xl border border-border-subtle bg-card shadow-sm',
        'transition-shadow hover:shadow-md focus-visible:outline focus-visible:outline-2',
        'focus-visible:outline-offset-2 focus-visible:outline-ring',
      )}
    >
      <div className="h-[5px]" style={{ background: cardAccentFor(card.eventId) }} aria-hidden />

      <div className="flex flex-col p-5">
        <div className="mb-3.5 flex items-start gap-3.5">
          <DateTile variant="soft" startsAt={card.startsAt} timezone={card.timezone} />
          <div className="min-w-0 flex-1">
            <h3 className="mb-1 text-lg leading-[1.2]">{card.name}</h3>
            <div className="text-xs text-muted-foreground">{when}</div>
          </div>
          {card.atmosphere === null ? null : (
            <span className="flex-none rounded-pill bg-muted px-[11px] py-[5px] font-brand text-2xs font-bold text-text-body">
              {card.atmosphere.displayName}
            </span>
          )}
        </div>

        {city === null && going === null ? null : (
          <div className="mb-3.5 flex flex-wrap items-center gap-x-3.5 gap-y-1 text-sm text-muted-foreground">
            {city === null ? null : (
              <span className="inline-flex items-center gap-[5px]">
                <MapPin className="h-3.5 w-3.5 shrink-0" aria-hidden />
                {city}
              </span>
            )}
            {going === null ? null : (
              <span className="inline-flex items-center gap-[5px]">
                <Users className="h-3.5 w-3.5 shrink-0" aria-hidden />
                {going}
              </span>
            )}
          </div>
        )}

        {card.interestTags.length === 0 ? null : (
          <ul className="mb-4 flex flex-wrap gap-1.5">
            {card.interestTags.map((tag) => (
              <li
                key={tag.termId}
                className="rounded-pill bg-muted px-2.5 py-[5px] font-brand text-xs font-semibold text-text-body"
              >
                {tag.displayName}
              </li>
            ))}
          </ul>
        )}

        {/* §3.2's footer sits on `margin-top:auto`, which on a card sized by its contents (see
            the `h-full` note above) is zero — the gap above the rule is the tags' own 16px and
            nothing more. `mt-4` added a second 16px, so every card carried 32px of air where the
            design draws 16 (parity pass, 2026-09-12). `mt-auto` keeps the prototype's behaviour
            if the card is ever stretched again. */}
        <div className="mt-auto flex items-center justify-between gap-3 border-t border-divider pt-3.5">
          {card.host === null ? (
            <span />
          ) : (
            <span className="inline-flex min-w-0 items-center gap-2">
              {/* Their uploaded mark where there is one, the gradient initials tile where there is
                  not — one component with the host row and the profile header, so a member does
                  not meet three ideas of what an organizer looks like (Sumit, 2026-09-12). */}
              <OrganizerMark
                name={card.host.name}
                logoUrl={card.host.logoUrl}
                className="h-[30px] w-[30px] text-[11px]"
              />
              <span className="truncate font-brand text-xs font-bold text-text-body">
                {card.host.name}
              </span>
            </span>
          )}
          <span className="inline-flex shrink-0 items-center gap-[5px] font-brand text-sm font-bold text-primary">
            View
            <ArrowRight className="h-4 w-4" aria-hidden />
          </span>
        </div>
      </div>
    </Link>
  );
}
